import { formatDollars } from '~/domains/common/utils/formatters';

import type {
  MarketPrice,
  Orderbook,
  PublicPredictionMarket,
} from '../services/interfaces/market.types';
import type { PusherOrderbook, PusherPriceLevel } from '../services/interfaces/market.pusher.types';
import { isMoneylineTwoWay } from '../services/interfaces/market.types';
import type { PriceDisplayFormat } from '../state/marketSettings.store';

import { stripFloatNoise } from './decimal';
import { isQuantitySettled, toWholeQuantity } from './quantity';

export type TeamType = 'home' | 'away';
export type Side = 'yes' | 'no';

export type MarketPriceSelection = {
  price: MarketPrice;
  side: Side;
  lineDisplay?: string;
};

export function getMarketPriceForTeam(
  market: PublicPredictionMarket,
  teamType: TeamType,
  homeTeamId: string,
  awayTeamId: string
): MarketPriceSelection | null {
  const teamId = teamType === 'home' ? homeTeamId : awayTeamId;

  if (market.type === 'moneyline') {
    if (isMoneylineTwoWay(market)) {
      if (market.yes_team_id === teamId) {
        return { price: market.price_yes, side: 'yes' };
      }
      if (market.no_team_id === teamId) {
        return { price: market.price_no, side: 'no' };
      }
      return null;
    }
    if (market.team_id === teamId) {
      return { price: market.price_yes, side: 'yes' };
    }
    return null;
  }

  if (market.type === 'spread') {
    const isYesSide = market.yes_team_id === teamId;
    return {
      price: isYesSide ? market.price_yes : market.price_no,
      side: isYesSide ? 'yes' : 'no',
      lineDisplay: isYesSide ? market.yes_line_display : market.no_line_display,
    };
  }

  if (market.type === 'total') {
    const isOver = teamType === 'away';
    return {
      price: isOver ? market.price_yes : market.price_no,
      side: isOver ? 'yes' : 'no',
      lineDisplay: isOver ? market.yes_line_display : market.no_line_display,
    };
  }

  return null;
}

export function getOrderbookSide(side: Side): 'yes_dollars' | 'no_dollars' {
  return side === 'yes' ? 'yes_dollars' : 'no_dollars';
}

export function decimalToAmerican(price: number): string {
  if (price === 0) {
    return '-10000';
  }

  const decimal = 1 / price;

  if (decimal <= 1.0) {
    return '-10000';
  }

  let american: number;

  if (decimal >= 2.0) {
    american = (decimal - 1) * 100;
  } else {
    american = -100 / (decimal - 1);
  }

  american = Math.round(american);

  return american > 0 ? `+${american}` : `${american}`;
}

export function createMarketPriceObject(price: number): MarketPrice {
  return {
    american: decimalToAmerican(price),
    decimal: price === 0 ? '0x' : `${(1 / price).toFixed(2)}x`,
    percentage: `${(price * 100).toFixed(0)}%`,
    price: `$${price.toFixed(2)}`,
    numeric_price: price,
  };
}

function floorToTwoDecimals(value: number): number {
  return Math.floor(stripFloatNoise(value * 100)) / 100;
}

function roundUpToTwoDecimals(value: number): number {
  return Math.ceil(stripFloatNoise(value * 100)) / 100;
}

export function createCostPerSharePriceObject(price: number): MarketPrice {
  return createMarketPriceObject(floorToTwoDecimals(price));
}

export type OrderbookSide = 'yes_dollars' | 'no_dollars';

type OrderbookOrder = {
  price: string;
  quantity: number;
};

function isPusherOrderbook(orderbook: PusherOrderbook | Orderbook): orderbook is PusherOrderbook {
  return 'asks' in orderbook && 'bids' in orderbook;
}

function transformPriceLevels(levels: PusherPriceLevel[]): OrderbookOrder[] {
  return levels.map((level) => ({
    price: String(level.p),
    quantity: level.q,
  }));
}

export function transformPusherOrderbook(orderbook: PusherOrderbook | Orderbook): Orderbook {
  if (!isPusherOrderbook(orderbook)) {
    return orderbook;
  }

  return {
    yes_dollars: transformPriceLevels(orderbook.asks.yes),
    no_dollars: transformPriceLevels(orderbook.asks.no),
  };
}

export function getAveragePrice(
  orderbook: Orderbook | PusherOrderbook | undefined,
  side: OrderbookSide,
  dollars: number
): number | null {
  if (!orderbook) return null;

  const normalized = transformPusherOrderbook(orderbook);
  const orders: OrderbookOrder[] = normalized[side] ?? [];

  if (orders.length === 0) return null;

  const sorted = [...orders].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

  let spent = 0;
  let contracts = 0;
  let remaining = dollars;

  for (const order of sorted) {
    if (remaining <= 0) break;

    const price = parseFloat(order.price);
    const canBuy =
      price > 0 ? Math.min(toWholeQuantity(remaining / price), toWholeQuantity(order.quantity)) : 0;
    const levelSpent = price * canBuy;

    spent += levelSpent;
    contracts += canBuy;
    remaining -= levelSpent;
  }

  return contracts > 0 ? roundUpToTwoDecimals(spent / contracts) : null;
}

type CalculateSharesResult = {
  shares: number;
  totalSpent: number;
  avgPricePerShareWithFee: number;
  shareValue: number;
  totalFee: number;
};

export function calculateShares(
  money: number,
  pricePerShare: number,
  feePerShare = 0
): CalculateSharesResult {
  const avgPricePerShareWithFee = pricePerShare + feePerShare;
  const shares = toWholeQuantity(money / avgPricePerShareWithFee);
  const shareValue = shares * pricePerShare;
  const totalFee = shares * feePerShare;
  const totalSpent = shares * avgPricePerShareWithFee;

  return {
    shares,
    totalSpent,
    avgPricePerShareWithFee,
    shareValue,
    totalFee,
  };
}

export function calculateToWinAmount(shares: number): number {
  return shares;
}

export function formatDollarAmount(amount: number): string {
  return formatDollars(amount, true);
}

export function getFormattedPrice(
  marketPrice: MarketPrice | undefined,
  format: PriceDisplayFormat
): string {
  if (!marketPrice) return '—';

  switch (format) {
    case 'percentage':
      return marketPrice.percentage;
    case 'american':
      return marketPrice.american;
    case 'price':
      return marketPrice.price;
    case 'decimal':
      return marketPrice.decimal;
    default:
      return marketPrice.percentage;
  }
}

export function formatNumericPrice(price: number, format: PriceDisplayFormat): string {
  return getFormattedPrice(createMarketPriceObject(price), format);
}

export function getNumericPrice(marketPrice: MarketPrice): number {
  if (marketPrice.numeric_price !== undefined && marketPrice.numeric_price !== null) {
    return marketPrice.numeric_price;
  }

  const percentageMatch = marketPrice.percentage?.match(/(\d+(?:\.\d+)?)/);
  if (percentageMatch) {
    return parseFloat(percentageMatch[1]) / 100;
  }

  return parseFloat(marketPrice.price.replace('$', '')) || 0;
}

export function calculateMaxBetFromOrderbook(
  orderbook: Orderbook | undefined,
  side: OrderbookSide,
  feePerShare = 0
): number {
  if (!orderbook) return 0;

  const orders: OrderbookOrder[] = orderbook[side] ?? [];

  if (orders.length === 0) return 0;

  let totalMaxBet = 0;
  let totalQuantity = 0;

  for (const order of orders) {
    const price = parseFloat(order.price);
    const quantity = toWholeQuantity(order.quantity);
    totalMaxBet += price * quantity;
    totalQuantity += quantity;
  }

  return totalMaxBet + totalQuantity * feePerShare;
}

export function calculateAveragePriceByQuantity(
  orderbook: Orderbook | PusherOrderbook | undefined,
  side: OrderbookSide,
  quantity: number
): number | null {
  if (!orderbook) return null;
  if (quantity <= 0) return null;

  const normalized = transformPusherOrderbook(orderbook);
  const orders: OrderbookOrder[] = normalized[side] ?? [];

  if (orders.length === 0) return null;
  const sorted = [...orders].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

  let spent = 0;
  let remaining = quantity;

  for (const order of sorted) {
    if (isQuantitySettled(remaining)) break;

    const price = parseFloat(order.price);
    const qty = order.quantity;

    const canBuy = Math.min(remaining, qty);
    spent += price * canBuy;
    remaining -= canBuy;
  }

  if (!isQuantitySettled(remaining)) return null;

  return roundUpToTwoDecimals(spent / quantity);
}

export function calculateOrderbookQuantityAvailable(
  orderbook: Orderbook | PusherOrderbook | undefined,
  side: OrderbookSide
): number {
  if (!orderbook) return 0;
  const normalized = transformPusherOrderbook(orderbook);
  const orders: OrderbookOrder[] = normalized[side] ?? [];
  return orders.reduce((sum, order) => sum + (order.quantity ?? 0), 0);
}

export function calculateOrderbookWholeContracts(
  orderbook: Orderbook | PusherOrderbook | undefined,
  side: OrderbookSide
): number {
  if (!orderbook) return 0;

  const normalized = transformPusherOrderbook(orderbook);
  const orders: OrderbookOrder[] = normalized[side] ?? [];

  return orders.reduce((sum, order) => sum + Math.floor(order.quantity ?? 0), 0);
}

export function calculateOrderbookLiquidityDollars(
  orderbook: Orderbook | PusherOrderbook | undefined,
  side: OrderbookSide
): number {
  if (!orderbook) return 0;

  const normalized = transformPusherOrderbook(orderbook);
  const orders: OrderbookOrder[] = normalized[side] ?? [];

  return orders.reduce((sum, order) => sum + parseFloat(order.price) * order.quantity, 0);
}
